// projectNormalizer.js
//
// Pure, total mapping from raw Projects API payloads to the client `Project`
// view model. These functions never throw: missing fields are given safe
// defaults and unexpected types are coerced, so a single malformed record can
// never fail the Projects page render (Requirements 6.2, 11.4).
//
// Project view model:
//   {
//     id: string,              // from job_id / id; coerced to string
//     title: string,           // raw.title; defaults to "Untitled project"
//     serviceCategory: string, // raw.ProjectTypes.type; defaults to "Other"
//     imageUrl: string | null, // null -> ProjectCard shows a brand placeholder
//     imageAlt: string,        // derived from title when absent
//     summary: string,         // short text for cards; defaults to ""
//     description: string      // detail body; defaults to ""
//   }

const DEFAULT_TITLE = 'Untitled project'
const DEFAULT_CATEGORY = 'Other'

/**
 * Coerce an arbitrary value into a trimmed string.
 * Returns '' for null/undefined and for values that cannot be sensibly
 * represented as text (objects/arrays/functions).
 */
function toCleanString (value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return String(value)
  // Objects, arrays, functions, NaN, Infinity, symbols, bigints etc.
  // are not meaningful display text -> treat as absent.
  return ''
}

/**
 * Resolve the project id from either `id` or `job_id`, coerced to a string.
 */
function resolveId (raw) {
  const id = toCleanString(raw.id)
  if (id !== '') return id
  return toCleanString(raw.job_id)
}

/**
 * Resolve the service category from `ProjectTypes.type`, defaulting to "Other".
 * Used for display (a single label, e.g. on the card overline).
 */
function resolveServiceCategory (raw) {
  const projectTypes = raw.ProjectTypes
  if (projectTypes && typeof projectTypes === 'object') {
    const type = toCleanString(projectTypes.type)
    if (type !== '') return type
  }
  const categories = resolveServiceCategories(raw)
  if (categories.length > 0) return categories[0]
  return DEFAULT_CATEGORY
}

/**
 * Resolve the full multi-select set of service categories from
 * `raw.serviceCategories` (an array of category name strings), falling back to
 * the single legacy `ProjectTypes.type` when absent. Always returns an array
 * (possibly empty); never throws.
 */
function resolveServiceCategories (raw) {
  const list = raw.serviceCategories
  if (Array.isArray(list)) {
    const cleaned = list.map(toCleanString).filter((v) => v !== '')
    if (cleaned.length > 0) return cleaned
  }
  const projectTypes = raw.ProjectTypes
  if (projectTypes && typeof projectTypes === 'object') {
    const type = toCleanString(projectTypes.type)
    if (type !== '') return [type]
  }
  return []
}

/**
 * Resolve the image URL, returning null when absent or not a usable string.
 */
function resolveImageUrl (raw) {
  const candidates = [raw.imageUrl, raw.image_url, raw.image]
  for (const candidate of candidates) {
    const url = toCleanString(candidate)
    if (url !== '') return url
  }
  return null
}

/**
 * Normalize a single raw API project into the `Project` view model.
 * Total: always returns a complete, correctly-typed object and never throws.
 *
 * @param {*} raw - any value from the Projects API (object, null, wrong type)
 * @returns {{id: string, title: string, serviceCategory: string,
 *   imageUrl: (string|null), imageAlt: string, summary: string,
 *   description: string}}
 */
export function normalizeProject (raw) {
  // Guard against null/non-object inputs so the function stays total.
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}

  const title = toCleanString(source.title) || DEFAULT_TITLE
  const serviceCategory = resolveServiceCategory(source)
  const serviceCategories = resolveServiceCategories(source)

  const imageUrl = resolveImageUrl(source)
  const imageAlt = toCleanString(source.imageAlt) || `${title} project image`

  const summary = toCleanString(source.summary)
  const description = toCleanString(source.description)

  return {
    id: resolveId(source),
    title,
    serviceCategory,
    serviceCategories,
    imageUrl,
    imageAlt,
    summary,
    description
  }
}

/**
 * Normalize a list of raw API projects. Tolerates a non-array input by
 * returning an empty array, and skips nothing — every element is normalized.
 *
 * @param {*} rawList - expected to be an array of raw projects
 * @returns {Array} normalized Project objects
 */
export function normalizeProjects (rawList) {
  if (!Array.isArray(rawList)) return []
  return rawList.map(normalizeProject)
}

export default normalizeProject
